import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { ChevronDown, ChevronUp, Settings, Check } from 'lucide-react';
import { user as userApi } from '../utils/api';

// Dietary preference options with emojis
const DIETARY_OPTIONS = [
  { id: 'vegetarian', label: 'Vegetarian', emoji: '🥬' },
  { id: 'vegan', label: 'Vegan', emoji: '🌱' },
  { id: 'gluten_free', label: 'Gluten-Free', emoji: '🌾' },
  { id: 'dairy_free', label: 'Dairy-Free', emoji: '🥛' },
  { id: 'nut_free', label: 'Nut-Free', emoji: '🥜' },
  { id: 'keto', label: 'Keto / Low-Carb', emoji: '🥑' },
  { id: 'heart_healthy', label: 'Heart-Healthy', emoji: '❤️' },
  { id: 'pescatarian', label: 'Pescatarian', emoji: '🐟' },
  { id: 'halal', label: 'Halal', emoji: '☪️' },
  { id: 'kosher', label: 'Kosher', emoji: '✡️' },
];

const FoodPreferences = forwardRef(({ onPreferencesChange, compact = false, defaultExpanded = false, onCollapse, onExpand, expandUp = false }, ref) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [preferences, setPreferences] = useState({
    dietary: [],
    additional_notes: ''
  });
  const [loading, setLoading] = useState(true);
  const hasChangesRef = useRef(false);

  // Sync with defaultExpanded prop when it changes
  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Notify parent of preference changes
  useEffect(() => {
    if (onPreferencesChange) {
      onPreferencesChange(preferences);
    }
  }, [preferences, onPreferencesChange]);

  const loadPreferences = async () => {
    try {
      const response = await userApi.getFoodPreferences();
      if (response.data.success && response.data.preferences) {
        setPreferences(response.data.preferences);
      }
    } catch (error) {
      console.error('Failed to load food preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Expose save method to parent via ref
  useImperativeHandle(ref, () => ({
    saveAndClose: async () => {
      if (hasChangesRef.current) {
        try {
          await userApi.updateFoodPreferences(preferences);
          hasChangesRef.current = false;
        } catch (error) {
          console.error('Failed to save food preferences:', error);
        }
      }
    }
  }));

  // Auto-save when collapsing if there are changes
  const handleToggleExpand = async () => {
    if (isExpanded && hasChangesRef.current) {
      // Collapsing with unsaved changes - auto-save
      try {
        await userApi.updateFoodPreferences(preferences);
        hasChangesRef.current = false;
      } catch (error) {
        console.error('Failed to save food preferences:', error);
      }
    }
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    // Notify parent when expanding or collapsing
    if (newExpanded && onExpand) {
      onExpand();
    }
    if (!newExpanded && onCollapse) {
      onCollapse();
    }
  };

  const toggleDietary = (id) => {
    setPreferences(prev => {
      const newDietary = prev.dietary.includes(id)
        ? prev.dietary.filter(d => d !== id)
        : [...prev.dietary, id];
      return { ...prev, dietary: newDietary };
    });
    hasChangesRef.current = true;
  };

  const updateNotes = (notes) => {
    setPreferences(prev => ({ ...prev, additional_notes: notes }));
    hasChangesRef.current = true;
  };

  // Generate summary text for collapsed view
  const getSummary = () => {
    const parts = [];
    if (preferences.dietary.length > 0) {
      const dietaryLabels = preferences.dietary.map(id => {
        const option = DIETARY_OPTIONS.find(o => o.id === id);
        return option ? option.label : id;
      });
      parts.push(dietaryLabels.join(', '));
    }
    if (preferences.additional_notes?.trim()) {
      parts.push('+ notes');
    }
    return parts.length > 0 ? parts.join(' ') : 'None set';
  };

  const activeCount = preferences.dietary.length + (preferences.additional_notes?.trim() ? 1 : 0);

  return (
    <div className={`bg-slate-100 border border-slate-200 rounded-lg ${compact ? 'text-sm' : ''} ${expandUp ? 'flex flex-col-reverse' : ''}`}>
      {/* Header - Always visible */}
      <button
        onClick={handleToggleExpand}
        className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-200 rounded-lg transition-colors ${expandUp && isExpanded ? 'border-t border-slate-200' : ''}`}
        disabled={loading}
      >
        <Settings size={compact ? 14 : 16} className={`flex-shrink-0 ${loading ? 'text-slate-300 animate-pulse' : 'text-slate-500'}`} />
        <span className="font-medium text-slate-600 whitespace-nowrap text-xs sm:text-sm">My Food Preferences</span>
        {activeCount > 0 && (
          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full whitespace-nowrap flex-shrink-0">
            {activeCount} active
          </span>
        )}
        {!isExpanded && (
          <span className="text-xs text-gray-500 truncate flex-1 text-left min-w-0">
            {getSummary()}
          </span>
        )}
        <div className="flex-shrink-0 ml-auto">
          {isExpanded ? (
            expandUp ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />
          ) : (
            expandUp ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className={`px-3 pb-3 ${expandUp ? 'pt-3' : 'border-t border-slate-200'}`}>
          {loading ? (
            <div className="py-4 text-center text-gray-400 text-sm animate-pulse">
              Loading preferences...
            </div>
          ) : (
            <>
              {/* Dietary toggles */}
              <div className={expandUp ? '' : 'mt-3'}>
                <p className="text-xs text-gray-500 mb-2">Dietary Restrictions</p>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => toggleDietary(option.id)}
                      className={`
                        flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all
                        ${preferences.dietary.includes(option.id)
                          ? 'bg-amber-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:border-amber-300'
                        }
                      `}
                    >
                      <span>{option.emoji}</span>
                      <span>{option.label}</span>
                      {preferences.dietary.includes(option.id) && (
                        <Check size={12} className="ml-0.5" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional notes */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Additional Notes (allergies, dislikes, etc.)</p>
                <textarea
                  value={preferences.additional_notes || ''}
                  onChange={(e) => updateNotes(e.target.value)}
                  placeholder="e.g., allergic to shellfish, no cilantro, prefer low-sugar..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  rows={2}
                />
              </div>

              {/* Info text */}
              <p className="mt-2 text-xs text-gray-400">
                Preferences auto-save when you close this panel.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default FoodPreferences;
